/**
 * Bibliotheek van geüploade documenten op het dossier (los van tekeningvoorstel).
 */
import { useRef, useState } from 'react';
import { Check, FileText, Loader2, Pencil, Trash2, Upload, X } from 'lucide-react';
import { isAcceptedImageOrPdfFile, libraryContentTypeForFile } from '../../lib/accept-upload-files';
import { api } from '../../lib/api';
import type { ProjectLibraryDocument } from '../../types/project';
import { Tooltip } from '../ui/Tooltip';

function newDocId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `doc_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function ProjectLibraryDocuments({
  projectId,
  tenantId,
  documents,
  onUpdated,
  btnSecondary,
}: {
  projectId: string;
  tenantId: string;
  documents: ProjectLibraryDocument[];
  onUpdated: (next: ProjectLibraryDocument[]) => Promise<void>;
  btnSecondary: string;
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [savingLabel, setSavingLabel] = useState(false);
  const uploadId = `lib-doc-${projectId}`;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = async (fileArray: File[]) => {
    if (fileArray.length === 0) {
      setError('Geen bestanden geselecteerd. Probeer het opnieuw of een ander bestand.');
      return;
    }
    setError(null);
    const list = fileArray;
    const ok = list.filter((f) => isAcceptedImageOrPdfFile(f));
    const skipped = list.length - ok.length;
    if (ok.length === 0) {
      setError(
        'Geen geldig bestandstype. Gebruik PNG, JPEG, WebP, GIF of PDF. Andere bestanden zijn overgeslagen.'
      );
      return;
    }
    setUploading(true);
    try {
      const next = [...documents];
      for (const file of ok) {
        const { url, key } = await api.uploadDocument(file, tenantId);
        next.push({
          doc_id: newDocId(),
          key,
          url,
          label: file.name,
          uploaded_at: new Date().toISOString(),
          content_type: libraryContentTypeForFile(file),
        });
      }
      await onUpdated(next);
      if (skipped > 0) {
        setError(
          `${ok.length} bestand(en) toegevoegd. ${skipped} bestand(en) overgeslagen (alleen afbeelding of PDF).`
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload mislukt');
    } finally {
      setUploading(false);
    }
  };

  const openAuthenticated = async (d: ProjectLibraryDocument) => {
    setError(null);
    setOpeningId(d.doc_id);
    try {
      const blob = await api.fetchDocumentBlob(d.url);
      const objectUrl = URL.createObjectURL(blob);
      const w = globalThis.window.open(objectUrl, '_blank', 'noopener,noreferrer');
      if (!w) {
        setError('Pop-up geblokkeerd. Sta pop-ups toe of gebruik “Opslaan als” via rechtermuisknop op de link.');
        URL.revokeObjectURL(objectUrl);
        return;
      }
      globalThis.window.setTimeout(() => URL.revokeObjectURL(objectUrl), 120_000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Openen mislukt');
    } finally {
      setOpeningId(null);
    }
  };

  const remove = async (docId: string) => {
    setError(null);
    try {
      await onUpdated(documents.filter((d) => d.doc_id !== docId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Verwijderen mislukt');
    }
  };

  const startEdit = (d: ProjectLibraryDocument) => {
    setEditingId(d.doc_id);
    setEditLabel(d.label || d.url);
    setError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditLabel('');
  };

  const saveLabel = async () => {
    if (!editingId) return;
    const label = editLabel.trim();
    if (!label) {
      setError('Vul een naam in.');
      return;
    }
    setSavingLabel(true);
    setError(null);
    try {
      await onUpdated(documents.map((d) => (d.doc_id === editingId ? { ...d, label } : d)));
      cancelEdit();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Naam opslaan mislukt');
    } finally {
      setSavingLabel(false);
    }
  };

  return (
    <div className="space-y-3">
      {error && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
          {error}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          id={uploadId}
          name={uploadId}
          className="sr-only"
          accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,.png,.jpg,.jpeg,.webp,.gif,.pdf"
          multiple
          onChange={(e) => {
            const picked = e.target.files ? Array.from(e.target.files) : [];
            e.target.value = '';
            void handleFiles(picked);
          }}
        />
        <button
          type="button"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
          className={`${btnSecondary} inline-flex cursor-pointer items-center gap-2 disabled:cursor-not-allowed disabled:opacity-60`}
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          Document toevoegen
        </button>
        <span className="inline-flex items-center gap-1 text-xs text-text-muted">
          PNG, JPEG, WebP, GIF of PDF — max. 10 MB per bestand
          <Tooltip
            iconOnly
            label="Waarom bestandsnamen?"
            content="Gebruik duidelijke namen (bijv. 01-bestaand-BG, 02-sloop, 03-installatie-E). Zo herkent de tekeninganalyse fase, verdieping of type plan beter. Je kunt namen altijd aanpassen met het potlood."
          />
        </span>
      </div>
      {documents.length === 0 ? (
        <p className="text-sm text-text-muted">Nog geen documenten in de bibliotheek.</p>
      ) : (
        <ul className="space-y-2">
          {documents.map((d) => (
            <li
              key={d.doc_id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border/70 bg-surface px-3 py-2 text-sm"
            >
              {editingId === d.doc_id ? (
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                  <input
                    type="text"
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                    className="min-w-[12rem] flex-1 rounded-md border border-border px-2 py-1 text-sm"
                    aria-label="Documentnaam"
                  />
                  <button
                    type="button"
                    disabled={savingLabel}
                    onClick={() => void saveLabel()}
                    className="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-xs font-medium text-white disabled:opacity-50"
                  >
                    <Check className="h-3.5 w-3.5" aria-hidden />
                    Opslaan
                  </button>
                  <button
                    type="button"
                    disabled={savingLabel}
                    onClick={cancelEdit}
                    className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs"
                  >
                    <X className="h-3.5 w-3.5" aria-hidden />
                    Annuleren
                  </button>
                </div>
              ) : (
                <>
                  <span className="flex min-w-0 items-center gap-2">
                    <FileText className="h-4 w-4 shrink-0 text-text-muted" aria-hidden />
                    <span className="truncate font-medium text-text">{d.label || 'Document'}</span>
                    <span className="text-xs text-text-muted">
                      {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString('nl-NL') : ''}
                    </span>
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => startEdit(d)}
                      className="rounded p-1 text-text-muted hover:bg-surface hover:text-text"
                      aria-label="Naam wijzigen"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      disabled={openingId === d.doc_id}
                      onClick={() => void openAuthenticated(d)}
                      className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
                    >
                      {openingId === d.doc_id ? 'Openen…' : 'Openen'}
                    </button>
                    <button
                      type="button"
                      onClick={() => void remove(d.doc_id)}
                      className="rounded p-1 text-red-600 hover:bg-red-50"
                      aria-label="Verwijderen uit bibliotheek"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
